import './campaign-links'

const MAX_RELEASE_NOTES_LENGTH = 48_000
const RELEASE_URL_PREFIX = 'https://github.com/hronaut/hronaut/releases/tag/'

interface ReleaseHistoryEntry {
  version: string
  title: string
  url: string
  publishedAt: string | null
  notes: string
}

interface ReleaseHistoryPayload {
  page: number
  hasMore: boolean
  releases: ReleaseHistoryEntry[]
  snapshot?: string
  generatedAt?: string
  truncated?: boolean
  olderHistoryUrl?: string
}

function validPage(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 1 && Number(value) <= 20
}

function parseEntry(value: unknown, snapshot?: string): ReleaseHistoryEntry {
  if (!value || typeof value !== 'object') throw new Error('Release entry must be an object')
  const candidate = value as Partial<ReleaseHistoryEntry>
  if (typeof candidate.version !== 'string' || !/^\d+\.\d+\.\d+$/u.test(candidate.version)) {
    throw new Error('Release version is invalid')
  }
  if (candidate.url !== `${RELEASE_URL_PREFIX}v${candidate.version}`) {
    throw new Error('Release URL is invalid')
  }
  if (
    typeof candidate.title !== 'string'
    || candidate.title.trim().length < 1
    || candidate.title.length > 120
  ) throw new Error('Release title is invalid')
  if (
    !(candidate.publishedAt === null && snapshot === `v${candidate.version}`)
    && (typeof candidate.publishedAt !== 'string' || !Number.isFinite(Date.parse(candidate.publishedAt)))
  ) throw new Error('Release published date is invalid')
  if (typeof candidate.notes !== 'string' || candidate.notes.length > MAX_RELEASE_NOTES_LENGTH) {
    throw new Error('Release notes are invalid')
  }
  return candidate as ReleaseHistoryEntry
}

export function parseReleaseHistoryPayload(value: unknown): ReleaseHistoryPayload {
  if (!value || typeof value !== 'object') throw new Error('Release history must be an object')
  const candidate = value as Partial<ReleaseHistoryPayload>
  if (!validPage(candidate.page)) throw new Error('Release history page is invalid')
  if (typeof candidate.hasMore !== 'boolean' || !Array.isArray(candidate.releases)) {
    throw new Error('Release history metadata is invalid')
  }
  if (candidate.snapshot !== undefined && (typeof candidate.snapshot !== 'string' || !/^v\d{1,6}\.\d{1,6}\.\d{1,6}$/u.test(candidate.snapshot) || typeof candidate.generatedAt !== 'string' || !Number.isFinite(Date.parse(candidate.generatedAt)))) {
    throw new Error('Release history snapshot metadata is invalid')
  }
  if (candidate.snapshot && (typeof candidate.truncated !== 'boolean' || (candidate.truncated ? candidate.olderHistoryUrl !== 'https://github.com/hronaut/hronaut/releases' : candidate.olderHistoryUrl !== undefined))) {
    throw new Error('Release history truncation metadata is invalid')
  }
  if (candidate.releases.length > 10 || new Set(candidate.releases.map(entry => entry?.version)).size !== candidate.releases.length) throw new Error('Release history entries are invalid')
  return {
    ...(candidate.snapshot ? { snapshot: candidate.snapshot, generatedAt: candidate.generatedAt, truncated: candidate.truncated, ...(candidate.truncated ? { olderHistoryUrl: candidate.olderHistoryUrl } : {}) } : {}),
    page: candidate.page,
    hasMore: candidate.hasMore,
    releases: candidate.releases.map(entry => parseEntry(entry, candidate.snapshot))
  }
}

function requireElement<ElementType extends HTMLElement>(selector: string): ElementType {
  const element = document.querySelector<ElementType>(selector)
  if (!element) throw new Error(`Missing release history element: ${selector}`)
  return element
}

function plainMarkdown(value: string): string {
  return value.replace(/[*_~`]/gu, '')
}

function safeHttpUrl(value: string): string | undefined {
  if (/\s/u.test(value)) return undefined
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined
  } catch {
    return undefined
  }
}

function appendText(parent: HTMLElement, value: string): void {
  if (!value) return
  const text = document.createElement('span')
  text.textContent = value
  parent.appendChild(text)
}

const CANONICAL_COMPARISON_URL = /https:\/\/github\.com\/hronaut\/hronaut\/compare\/v[0-9A-Za-z.+-]+\.{3}v[0-9A-Za-z.+-]+/gu

function appendPlainText(parent: HTMLElement, value: string): void {
  const text = plainMarkdown(value)
  let offset = 0
  for (const match of text.matchAll(CANONICAL_COMPARISON_URL)) {
    const url = match[0]
    const index = match.index
    appendText(parent, text.slice(offset, index))
    const link = document.createElement('a')
    link.href = url
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.textContent = url
    parent.appendChild(link)
    offset = index + url.length
  }
  appendText(parent, text.slice(offset))
}

function appendInlineMarkdown(parent: HTMLElement, value: string): void {
  let offset = 0
  while (offset < value.length) {
    const labelStart = value.indexOf('[', offset)
    if (labelStart < 0) break
    const destinationStartMarker = value.indexOf('](', labelStart + 1)
    if (destinationStartMarker < 0) break
    let depth = 0
    let destinationEnd = -1
    for (let index = destinationStartMarker + 2; index < value.length; index += 1) {
      const character = value[index]
      if (character === '(') depth += 1
      if (character !== ')') continue
      if (depth > 0) {
        depth -= 1
        continue
      }
      destinationEnd = index
      break
    }
    if (destinationEnd < 0) break

    appendPlainText(parent, value.slice(offset, labelStart))
    const label = value.slice(labelStart + 1, destinationStartMarker)
    const destination = value.slice(destinationStartMarker + 2, destinationEnd)
    const url = safeHttpUrl(destination)
    if (label && url) {
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      link.textContent = plainMarkdown(label).trim()
      parent.appendChild(link)
    } else if (label) {
      appendPlainText(parent, label)
    }
    offset = destinationEnd + 1
  }
  appendPlainText(parent, value.slice(offset))
}

function renderNotes(notes: string): HTMLElement {
  const container = document.createElement('div')
  container.className = 'public-release-notes'
  if (!notes) {
    const empty = document.createElement('p')
    empty.textContent = 'No detailed notes were published for this release.'
    container.appendChild(empty)
    return container
  }

  let list: HTMLUListElement | null = null
  let callout: HTMLElement | null = null
  for (const rawLine of notes.split(/\r?\n/u)) {
    const line = rawLine.trim()
    if (!line) {
      list = null
      callout = null
      continue
    }
    const calloutMarker = /^>\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*$/iu.exec(line)
    if (calloutMarker?.[1]) {
      list = null
      const kind = calloutMarker[1].toLowerCase()
      callout = document.createElement('aside')
      callout.className = `public-release-callout public-release-callout--${kind}`
      const label = document.createElement('strong')
      label.textContent = kind.charAt(0).toUpperCase() + kind.slice(1)
      callout.appendChild(label)
      container.appendChild(callout)
      continue
    }
    if (callout && /^>/u.test(line)) {
      const paragraph = document.createElement('p')
      appendInlineMarkdown(paragraph, line.replace(/^>\s?/u, ''))
      callout.appendChild(paragraph)
      continue
    }
    callout = null
    if (/^_{3,}$|^-{3,}$|^\*{3,}$/u.test(line)) {
      list = null
      const divider = document.createElement('hr')
      divider.className = 'public-release-divider'
      container.appendChild(divider)
      continue
    }
    const heading = /^(#{1,6})\s+(.+)$/u.exec(line)
    if (heading?.[2]) {
      list = null
      const title = document.createElement((heading[1]?.length ?? 0) <= 3 ? 'h3' : 'h4')
      appendInlineMarkdown(title, heading[2])
      container.appendChild(title)
      continue
    }
    const bullet = /^[-*]\s+(.+)$/u.exec(line)
    if (bullet?.[1]) {
      if (!list) {
        list = document.createElement('ul')
        container.appendChild(list)
      }
      const item = document.createElement('li')
      appendInlineMarkdown(item, bullet[1])
      list.appendChild(item)
      continue
    }
    list = null
    const paragraph = document.createElement('p')
    appendInlineMarkdown(paragraph, line.replace(/^>\s?/u, ''))
    container.appendChild(paragraph)
  }
  return container
}

function renderRelease(release: ReleaseHistoryEntry): HTMLElement {
  const article = document.createElement('article')
  article.className = 'public-release-card'

  const header = document.createElement('header')
  const copy = document.createElement('div')
  const date = document.createElement(release.publishedAt ? 'time' : 'span')
  if (release.publishedAt) {
    date.setAttribute('datetime', release.publishedAt)
    date.textContent = new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(new Date(release.publishedAt))
  } else {
    date.textContent = 'Latest release'
  }
  const title = document.createElement('h2')
  title.textContent = release.title
  copy.appendChild(date)
  copy.appendChild(title)

  const link = document.createElement('a')
  link.href = release.url
  link.rel = 'noreferrer'
  link.textContent = `v${release.version} ↗`
  link.setAttribute('aria-label', `Open Hronaut ${release.version} on GitHub`)
  header.appendChild(copy)
  header.appendChild(link)
  article.appendChild(header)
  article.appendChild(renderNotes(release.notes))
  return article
}

export async function initializeReleaseHistory(): Promise<void> {
  const status = requireElement<HTMLElement>('#release-history-status')
  const retry = requireElement<HTMLButtonElement>('#release-history-retry')
  const list = requireElement<HTMLElement>('#release-history-list')
  const loadMore = requireElement<HTMLButtonElement>('#release-history-more')
  let currentPage = 0
  let loading = false
  let snapshot: string | undefined

  const load = async (page: number): Promise<void> => {
    if (loading) return
    loading = true
    retry.hidden = true
    loadMore.disabled = true
    status.hidden = false
    status.textContent = page === 1 ? 'Loading release history…' : 'Loading older releases…'
    if (page === 1) { list.replaceChildren(); snapshot = undefined }
    try {
      const source = page === 1 ? '' : snapshot ? `&snapshot=${encodeURIComponent(snapshot)}` : '&source=api'
      const response = await fetch(`/api/releases?page=${page}${source}`, {
        cache: 'no-store',
        headers: { accept: 'application/json' }
      })
      if (!response.ok) throw new Error(`Release history request failed with ${response.status}`)
      const history = parseReleaseHistoryPayload(await response.json())
      if (history.page !== page) throw new Error('Release history returned the wrong page')
      if (page > 1 && history.snapshot !== snapshot) throw new Error('Release history changed source during pagination')
      snapshot = history.snapshot
      for (const release of history.releases) list.appendChild(renderRelease(release))
      currentPage = history.page
      status.hidden = !snapshot && (history.releases.length > 0 || currentPage > 1)
      status.textContent = snapshot ? `Showing recent history included with ${snapshot}.` : history.releases.length === 0 && currentPage === 1
        ? 'No public releases are available yet.'
        : ''
      if (history.truncated && history.olderHistoryUrl) {
        const older = document.createElement('a')
        older.href = history.olderHistoryUrl
        older.rel = 'noreferrer'
        older.textContent = ' View older history on GitHub.'
        status.appendChild(older)
      }
      loadMore.hidden = !history.hasMore
    } catch (error) {
      console.warn('Unable to load Hronaut release history.', error)
      status.hidden = false
      status.textContent = 'Release history is temporarily unavailable. You can still view every release on GitHub.'
      retry.hidden = false
      loadMore.hidden = true
    } finally {
      loading = false
      loadMore.disabled = false
    }
  }

  retry.addEventListener('click', () => void load(1))
  loadMore.addEventListener('click', () => void load(currentPage + 1))
  await load(1)
}

if (typeof document !== 'undefined') void initializeReleaseHistory()
